import { BitkitLink, useResponsive } from '@bitrise/bitkit-v2';
import { useEffect, useState } from 'react';

import LoginButtonGroup from '@/components/buttons/LoginButtonGroup/LoginButtonGroup';
import LoginCard from '@/components/LoginCard/LoginCard';
import AuthPage from '@/components/page/AuthPage/AuthPage';
import useQueryParams from '@/hooks/useQueryParams';
import SSOEmailForm from '@/pages/SignIn/SignInPage/SSOEmailForm';
import { setCookie } from '@/utils/cookies';
import { setLoginAttempted } from '@/utils/loginTracking';

import useSignUpFlashMessage from '../hooks/useSignUpFlashMessage';
import SignUpForm from './components/SignUpForm';
import useProductIntent, { ProductIntent, productIntentConfigurations } from './useProductIntent';

type SignUpFormStateType = 'selection' | 'email' | 'sso';

const SignUpPage = ({
  initialFormState,
  prefillEmail: prefillEmailProp,
  forcePrefilled: forcePrefilledProp,
  productIntent: productIntentProp,
}: {
  initialFormState?: SignUpFormStateType;
  prefillEmail?: string;
  forcePrefilled?: boolean;
  productIntent?: ProductIntent;
}) => {
  const queryParams: URLSearchParams = useQueryParams();
  const prefillEmail = prefillEmailProp ?? queryParams.get('email') ?? undefined;
  const forcePrefilled = forcePrefilledProp ?? queryParams.has('force_email');
  const productIntent = useProductIntent(productIntentProp);
  const { isMobile } = useResponsive();

  useSignUpFlashMessage();

  const [formState, setFormState] = useState<SignUpFormStateType>(
    prefillEmail ? 'email' : (initialFormState ?? 'selection'),
  );

  useEffect(() => {
    setLoginAttempted();
  }, []);

  useEffect(() => {
    if (productIntent) {
      const config = productIntentConfigurations[productIntent];
      setCookie('sign_in_redirect', JSON.stringify({ return_to: config.destinationPath }), {
        cookieOptions: { path: '/', expires: 30, sameSite: 'Lax' as const },
      });
    }
  }, [productIntent]);

  const ssoButtonProps = {
    onClick: () => setFormState('sso'),
  };

  const backButtonProps = {
    onBackButtonClick: () => setFormState('selection'),
    backButtonLabel: 'Back to all sign up methods',
  };

  return (
    <AuthPage
      title="Sign up, start building"
      subtitle="Speed up your development, release with confidence"
      bottomMessage={
        <>
          Already have an account?{' '}
          <BitkitLink href="/users/sign_in" textDecoration="underline">
            Log in
          </BitkitLink>
        </>
      }
      productIntent={productIntent}
    >
      {formState === 'selection' && (
        <LoginCard variant={productIntent && isMobile ? 'default' : 'transparent'}>
          <LoginButtonGroup
            ssoButtonProps={ssoButtonProps}
            onEmailSelect={() => setFormState('email')}
            signUpMode
            onTransparentBackground={productIntent && isMobile ? false : true}
          />
        </LoginCard>
      )}
      {formState === 'email' && (
        <LoginCard title="Sign up with Email" {...backButtonProps}>
          <SignUpForm prefillEmail={prefillEmail} forcePrefilled={forcePrefilled} />
        </LoginCard>
      )}
      {formState === 'sso' && (
        <LoginCard title="Sign up with SSO" {...backButtonProps}>
          <SSOEmailForm requireConsent />
        </LoginCard>
      )}
    </AuthPage>
  );
};

export default SignUpPage;
